//src/utils/readings.ts
//
// Tach and Hobbs readings — ONE place for how they're kept. Always stored as
// NUMBERS (form fields can hand back text; everything that saves a reading
// goes through these, so text never reaches the database):
//   Tach  — the aircraft's own tach precision (equipment.tachPrecision):
//           tenths or hundredths; at most 2 decimal places.
//   Hobbs — tenths: Hobbs meters read tenths only.
// Flight times (end − start) follow the same rule as the meter they come from.

/** Decimal places a tach reading is kept to, at most (hundredths). */
export const TACH_PLACES = 2

/** How finely an aircraft's tach reads — set on its equipment. */
export type TachPrecision = 'tenths' | 'hundredths'

/** Default: hundredths, so no existing reading is ever cut. */
export const DEFAULT_TACH_PRECISION: TachPrecision = 'hundredths'

/**
 * Decimal places for THIS aircraft's tach: 1 (tenths) or 2 (hundredths).
 * Takes the aircraft (or anything with its equipment); unknown → hundredths.
 */
export function tachPlacesFor(
  aircraft: { equipment?: { tachPrecision?: TachPrecision } } | null | undefined
): number {
  return aircraft?.equipment?.tachPrecision === 'tenths' ? 1 : TACH_PLACES
}
/** Decimal places a Hobbs reading is kept to (Hobbs meters read tenths). */
export const HOBBS_PLACES = 1

function rounded(v: unknown, places: number): number | null {
  if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) return null
  const n = Number(v)
  if (!Number.isFinite(n)) return null
  const factor = 10 ** places
  return Math.round(n * factor) / factor
}

/** A tach reading as a number, to `places` decimals (default 2). Blank or invalid → 0. */
export function toTach(v: unknown, places: number = TACH_PLACES): number {
  return rounded(v, places) ?? 0
}

/** A Hobbs reading as a number, in tenths. Blank or invalid → 0. */
export function toHobbs(v: unknown): number {
  return rounded(v, HOBBS_PLACES) ?? 0
}

/** Same as toTach, but blank stays null (e.g. an anchor that wasn't set). */
export function toTachOrNull(v: unknown, places: number = TACH_PLACES): number | null {
  return rounded(v, places)
}

/** Same as toHobbs, but blank stays null. */
export function toHobbsOrNull(v: unknown): number | null {
  return rounded(v, HOBBS_PLACES)
}

/** 'hobbs' / 'tach' for a field holding a reading (hobbs, tach, …Hobbs, …Tach), else null. */
export function readingKind(key: string): 'hobbs' | 'tach' | null {
  if (key === 'hobbs' || /Hobbs$/.test(key)) return 'hobbs'
  if (key === 'tach' || /Tach$/.test(key)) return 'tach'
  return null
}

/**
 * A copy of a status object (e.g. maintenanceToDate) with every reading
 * field, at any depth, as a number — Hobbs fields in tenths, tach fields to
 * 2 places. Blank stays empty (null). Other fields are left as they are.
 */
export function withNumericReadings<T>(value: T): T {
  if (Array.isArray(value)) return value.map(withNumericReadings) as T
  if (!value || typeof value !== 'object' || value instanceof Date) return value
  if (typeof (value as { toDate?: unknown }).toDate === 'function') return value // Timestamp
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const kind = readingKind(k)
    out[k] =
      kind && (typeof v === 'string' || typeof v === 'number')
        ? kind === 'hobbs'
          ? toHobbsOrNull(v)
          : toTachOrNull(v)
        : withNumericReadings(v)
  }
  return out as T
}
